import { useEffect, useState } from 'react'
import { fetchClienteSession } from '@/api/usuariosApi'
import { useUser } from '@/contexts/UserContext'
import { isAdmin } from '@/utils/adminAuth'
import { clearClienteToken, hasClienteSessionHint } from '@/utils/clienteAuth'
import { clearGuestSession, isGuestSessionActive } from '@/utils/guestAuth'

export type ClienteSessionGate = 'checking' | 'allowed' | 'denied'

function initialGate(): ClienteSessionGate {
  if (isAdmin()) return 'allowed'
  if (isGuestSessionActive() && !hasClienteSessionHint()) return 'allowed'
  if (!hasClienteSessionHint() && !isGuestSessionActive()) return 'denied'
  return 'checking'
}

/**
 * Valida a sessão do cliente contra GET /api/usuarios/session (cookie httpOnly).
 */
export function useClienteSessionValidation(): ClienteSessionGate {
  const { user, setUser } = useUser()
  const [gate, setGate] = useState<ClienteSessionGate>(initialGate)

  useEffect(() => {
    if (isAdmin()) {
      setGate('allowed')
      return
    }

    // Já há utilizador em memória (checkout / login recente)
    if (user) {
      setGate('allowed')
      return
    }

    if (isGuestSessionActive() && !hasClienteSessionHint()) {
      setGate('allowed')
      return
    }

    if (!hasClienteSessionHint() && !isGuestSessionActive()) {
      setGate('denied')
      return
    }

    let cancelled = false
    setGate('checking')

    fetchClienteSession()
      .then((sessionUser) => {
        if (cancelled) return
        if (sessionUser) {
          setUser({
            id: sessionUser.id,
            nome: sessionUser.nome,
            telefone: sessionUser.telefone,
            email: sessionUser.email,
            avatar_url: sessionUser.avatar_url ?? null,
          })
          setGate('allowed')
          return
        }
        // Sem cookie/Bearer válido — se ainda for visitante, mantém acesso
        if (isGuestSessionActive()) {
          clearClienteToken()
          setGate('allowed')
          return
        }
        clearClienteToken()
        clearGuestSession()
        setUser(null)
        setGate('denied')
      })
      .catch(() => {
        if (cancelled) return
        if (isGuestSessionActive()) {
          setGate('allowed')
          return
        }
        clearClienteToken()
        clearGuestSession()
        setUser(null)
        setGate('denied')
      })

    return () => {
      cancelled = true
    }
  }, [setUser, user])

  return gate
}
